
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import moment from 'moment';
import {
  Task, Lead, PermissionCode, LeadType, TaskStatus, LeadStatus, ProjectsPool, User, BoxStatusType,
} from '@/types';
import TaskStatusAlert from '@/components/TaskStatusAlert.vue';
import TaskActions from '@/components/TaskActions.vue';
import SwitchWithConfirm from '@/components/SwitchWithConfirm.vue';
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import WeekPickerDialog from '@/components/WeekPickerDialog.vue';
import FullDatePickerDialog from '@/components/FullDatePickerDialog.vue';
import {
  currentUserHasPermission, taskFatherLead,
  getLeadCustomValueBoolean, getLeadCustomValueText, getLeadCustomValueDate,
} from '@/helpers';
import CopyToClipboardIcon from '@/components/CopyToClipboardIcon.vue';
import AttachmentsList from '@/components/AttachmentsList.vue';
import MissingFinishings from '@/components/MissingFinishings.vue';
import UTILS from '@/lib/UTILS';
import API from '@/lib/API';

moment.locale('it');

@Component({
  components: {
    TaskStatusAlert,
    TaskActions,
    SwitchWithConfirm,
    DatePickerDialog,
    WeekPickerDialog,
    FullDatePickerDialog,
    CopyToClipboardIcon,
    AttachmentsList,
    MissingFinishings,
  },
})
export default class TaskI extends Vue {
  @Prop(Object) readonly task: Task | undefined;
  @Prop({ type: Boolean, default: false }) readonly embedded!: boolean;
  @Prop({ type: Boolean, default: false }) readonly readonly!: boolean;

  // Il form è valido?
  isFormValid = false

  selectedStatus: 'completed' | 'error' = 'completed'

  subDefined = false
  subSent = false
  subSigned = false
  subNotes: string | null = null

  partialInstallationDate: Date | null = null
  partialInstallationDateMode: 'single' | 'week' | null = null
  partialInstallationDateConfirm = false
  partialInstallationCompleted = false
  partialInstallationSalPaymentReady = false;

  installationDate: Date | null = null
  installationDateMode: 'single' | 'week' | null = null
  installationDateConfirm = false
  installationCompleted = false
  installationCompletedDate: Date | null = null
  installationCompletedUpdatedBy: string | null = null

  installationNotes: string | null = null

  missingFinishingsStatus: BoxStatusType | null = null
  missingFinishingsNotes: string | null = null

  datePickerDialog = false

  weekPickerDialog = false
  allowSingle = true
  allowWeek = true
  initialMode: 'single' | 'week' = 'single'

  fullDatePickerDialog = false

  datePickerDate: string | null = null
  datePickerName: string | null = null

  operationId = this.father?.id;

  created() {
    console.log(`${this.constructor.name}: created()`); // eslint-disable-line no-console

    // Inizializza tutti i campi del modulo
    this.refreshTask();
  }

  // Aggiorna i campi con gli eventuali nuovi valori della Lead selezionata
  async refreshTask() {
    console.log(`${this.constructor.name}: refreshTask()`); // eslint-disable-line no-console

    this.subDefined = getLeadCustomValueBoolean(this.selectedLead, 'subDefined') ?? false;
    this.subSent = getLeadCustomValueBoolean(this.selectedLead, 'subSent') ?? false;
    this.subSigned = getLeadCustomValueBoolean(this.selectedLead, 'subSigned') ?? false;
    this.subNotes = getLeadCustomValueText(this.selectedLead, 'subNotes');

    this.partialInstallationDate = getLeadCustomValueDate(this.selectedLead, 'partialInstallationDate');
    this.partialInstallationDateMode = getLeadCustomValueText(this.selectedLead, 'partialInstallationDateMode') as 'single' | 'week' | null;
    this.partialInstallationDateConfirm = getLeadCustomValueBoolean(this.selectedLead, 'partialInstallationDateConfirm') ?? false;

    this.partialInstallationCompleted = getLeadCustomValueBoolean(this.selectedLead, 'partialInstallationCompleted') ?? false;
    this.partialInstallationSalPaymentReady = getLeadCustomValueBoolean(this.selectedLead, 'partialInstallationSalPaymentReady') ?? false;

    this.installationDate = getLeadCustomValueDate(this.selectedLead, 'installationDate');
    this.installationDateMode = getLeadCustomValueText(this.selectedLead, 'installationDateMode') as 'single' | 'week' | null;
    this.installationDateConfirm = getLeadCustomValueBoolean(this.selectedLead, 'installationDateConfirm') ?? false;

    this.installationCompleted = getLeadCustomValueBoolean(this.selectedLead, 'installationCompleted') ?? false;
    this.installationCompletedDate = getLeadCustomValueDate(this.selectedLead, 'installationCompletedDate');
    this.installationCompletedUpdatedBy = await this.getUserNameFromCustomValue('installationCompletedUpdatedBy');

    this.installationNotes = getLeadCustomValueText(this.selectedLead, 'installationNotes');

    this.missingFinishingsStatus = getLeadCustomValueText(this.selectedLead, 'missingFinishingsStatus') as BoxStatusType | null;
    this.missingFinishingsNotes = getLeadCustomValueText(this.selectedLead, 'missingFinishingsNotes');
  }

  // Il Task è valido quando sia il form che i dati extra lo sono
  get valid(): boolean {
    return this.isFormValid && this.isExtraDataValid;
  }

  // I dati extra sono validi?
  get isExtraDataValid(): boolean {
    // Se è impossibile completare il task, i dati extra sono ignorati
    // FIXME: Rimuovere quando questo Task userà il mixin TaskMixin
    if (this.selectedStatus === 'error') return true;

    // In tutti gli altri casi...
    return true;
  }

  // Se la validità del form cambia, la memorizza nello stato di Vuex
  @Watch('valid')
  onValidChange(val: boolean) {
    console.log(`${this.constructor.name}: onValidChange() val =`, val); // eslint-disable-line no-console

    this.$store.dispatch('updateTaskFieldsValidity', { value: val });
  }

  get selectedLead(): Lead | null {
    const childLeads: ProjectsPool = this.$store.getters.currentChildren;
    const childLeadsInfo: ProjectsPool = this.$store.getters.currentChildrenInfo;

    // Se è stato specificato un Task e questo è associato ad una Lead figlia...
    if (this.task && this.task.relationship === 'father') {
      // ... se la lead figlia non è stata ancora assegnata,
      // non ritorna nulla.
      if ('childLeadId' in this.task === false || !this.task.childLeadId) return null;

      // ... se la lead figlia è stata assegnata,
      // considera i suoi dati (se disponibili,
      // altrimenti utilizza i dati limitati di childLeadsInfo)
      return childLeads[this.task.childLeadId] ?? childLeadsInfo[this.task.childLeadId] ?? null;
    }

    // Negli altri casi ritorna la Lead selezionata memorizzata tramite Vuex
    return this.$store.getters.currentLead;
  }

  get currentUser(): User {
    return this.$store.getters.currentUser;
  }

  get selectedTask(): Task | null {
    // Se non è stato specificato un Task nelle Prop,
    // utilizza il Task selezionato memorizzato tramite Vuex.
    return this.task ?? this.$store.getters.selectedTask;
  }

  get loading(): boolean {
    // Se è stato specificato un Task nelle Prop,
    // non utilizza questo boolean (sempre false).
    return this.task
      ? false
      : this.$store.getters.isTaskLoading;
  }

  get updating(): boolean {
    // Se è stato specificato un Task nelle Prop,
    // non utilizza questo boolean (sempre false).
    return this.task
      ? false
      : this.$store.getters.isTaskUpdating;
  }

  // Ritorma true se è in corso l'aggiornamento della Lead mostrata
  get leadUpdating(): boolean { return this.$store.getters.isProjectUpdating; }

  get calendarUrl(): string {
    const token = this.currentUser?.iCalToken;

    // Ottiene l'URL del server corrente
    const url = window.location.host;

    // Ottiene il protocollo del server corrente (ad esempio "https:" o "http:")
    // eslint-disable-next-line prefer-destructuring
    const protocol = window.location.protocol;

    return `${protocol}//${url}/api/operations/calendar/${token}/installation`;
  }

  get calendarUrlNoCommissioning(): string {
    const token = this.currentUser?.iCalToken;

    // Ottiene l'URL del server corrente
    const url = window.location.host;

    // Ottiene il protocollo del server corrente (ad esempio "https:" o "http:")
    // eslint-disable-next-line prefer-destructuring
    const protocol = window.location.protocol;

    return `${protocol}//${url}/api/operations/calendar/${token}/installation-no-commissioning`;
  }

  get calendarOptions(): { text: string; url: string }[] {
    return [{
      text: 'Tutte le installazioni',
      url: this.calendarUrl,
    }, {
      text: 'Installazioni senza commissioning',
      url: this.calendarUrlNoCommissioning,
    }];
  }

  // Tiene sotto controllo leadUpdateing per capire quando eventualmente
  // vanno aggiornati i dati visualizzati.
  @Watch('leadUpdating')
  onLeadUpdatingChange(val: boolean, oldVal: boolean) {
    console.log(`${this.constructor.name}: onLeadUpdatingChange() val = ${val} oldVal = ${oldVal}`); // eslint-disable-line no-console

    // Se leadUpdating passa da true a folse,
    // la Lead ha completato l'aggiornamento e vanno aggiornati i dati visualizzati.
    if (val === false && oldVal === true) {
      // Aggiorna la Lead con i nuovi valori soltanto se è in visualizzazione readonly
      if (this.readonly) this.refreshTask();
    }
  }

  get leadType(): LeadType | null { return this.selectedLead?.type ?? null; }

  get leadStatus(): LeadStatus | null { return this.selectedLead?.status ?? null; }

  get taskStatus(): TaskStatus { return this.selectedTask?.status ?? null; }

  get father(): Lead | null { return taskFatherLead(this.selectedTask); }

  get form(): Vue & { validate: () => boolean; reset: () => boolean } {
    return this.$refs.form as Vue & {
      validate: () => boolean;
      reset: () => boolean;
    };
  }

  get appendIcon(): string | null { return this.fieldsDisabled ? 'mdi-lock' : null; }

  get subValue(): null | 'defined' | 'sent' | 'signed' {
    if (this.subSigned) return 'signed';
    if (this.subSent) return 'sent';
    if (this.subDefined) return 'defined';
    return null;
  }
  set subValue(value: null | 'defined' | 'sent' | 'signed') {
    if (value === 'signed') {
      this.subDefined = true;
      this.subSent = true;
      this.subSigned = true;
    } else if (value === 'sent') {
      this.subDefined = true;
      this.subSent = true;
      this.subSigned = false;
    } else if (value === 'defined') {
      this.subDefined = true;
      this.subSent = false;
      this.subSigned = false;
    } else {
      this.subDefined = false;
      this.subSent = false;
      this.subSigned = false;
    }
  }

  get fieldsDisabled(): boolean {
    return (this.task !== null && this.task !== undefined)
      || this.selectedTask?.status !== null
      || this.readonly
      || this.loading
      || this.updating;
  }

  // Mostra le azioni nell'Alert soltanto sulle lead figlie e sui Task senza figlie
  get showAlertActions(): boolean { return this.task?.relationship !== 'father'; }

  // Mostra le azioni
  get showActions(): boolean { return !this.task && !this.readonly && !this.embedded; }

  mDate(date: Date | null | undefined): string | null { return date ? moment(date).format('L') : null; }

  mWeek(date: Date | null | undefined): string | null {
    return date ? `Settimana ${moment(date).format('WW')}-${moment(date).format('GGGG')}` : null;
  }

  get partialInstallationDateText(): string | null {
    if (this.partialInstallationDateMode === 'single') {
      return this.mDate(this.partialInstallationDate);
    }

    // this.partialInstallationDateMode === 'week' || this.partialInstallationDateMode === null
    return this.mWeek(this.partialInstallationDate);
  }

  get installationDateText(): string | null {
    if (this.installationDateMode === 'single') {
      return this.mDate(this.installationDate);
    }

    // this.installationDateMode === 'week' || this.installationDateMode === null
    return this.mWeek(this.installationDate);
  }

  // FIXME: Implementare tramite Vuex
  async getUserNameFromCustomValue(name: string): Promise<string | null> {
    if (!this.selectedLead) return null;

    const userId = UTILS.getCustomValueUserIdByName(this.selectedLead, name) ?? null;
    const userName = userId ? await API.getUserName(userId) : null;
    return userName;
  }

  updatedAtLong(updatedAt: Date | null, updatedBy: string | null) {
    return updatedAt && updatedBy
      ? `Ultima modifica: ${moment(updatedAt).format('L')} ore ${moment(updatedAt).format('LT')} `
        + `(${updatedBy ?? 'Sconosciuto'})`
      : '';
  }

  hasPermission(codename: PermissionCode) { return currentUserHasPermission(codename); }

  onSwitchChange(name: string) {
    console.log(`${this.constructor.name}: onSwitchChange(${name})`); // eslint-disable-line no-console

    // Se lo switch non esiste si ferma subito
    if (name in this === false) { return; }

    // Il valore del modello dello switch è già stato aggiornato.
    // Proseguo aggiornando eventuali valori collegati e preparo l'aggiornamento tramite API.

    const update = {} as { [key: string]: unknown };

    update[name] = this[name];

    // Modifico eventuali campi collegati
    switch (name) {
      case 'partialInstallationDateConfirm':
        if (this[name] === false) {
          // È stata rimossa la conferma della data concordata per l'installazione parziale,
          // quindi rimuove anche il completamento dell'installazione parziale.
          this.partialInstallationCompleted = false;
          update.partialInstallationCompleted = false;
        }

        if (this[name] === true) {
          // È stata confermata la data concordata per l'installazione parziale,
          // apre il dialog per la selezione della data in modalità "selezione singola".
          this.onClickDate('partialInstallationDate', true);
        }

        break;

      case 'installationDateConfirm':
        if (this[name] === false) {
          // È stata rimossa la conferma della data concordata per l'installazione finale,
          // quindi rimuove anche il completamento dell'installazione finale.
          this.installationCompleted = false;
          update.installationCompleted = false;
        }

        if (this[name] === true) {
          // È stata confermata la data concordata per l'installazione finale,
          // Apre il dialog per la selezione della data in modalità "selezione singola"
          this.onClickDate('installationDate', true);
        }

        break;

      case 'installationCompleted':
        // Memorizzo la data attuale e l'utente che ha fatto la modifica
        this.installationCompletedDate = new Date();
        this.installationCompletedUpdatedBy = this.currentUser.name;
        break;

      default:
        break;
    }

    this.$store.dispatch('updateTaskFields', update);
  }

  onClickDate(name: string, forceSingle = false) {
    console.log(`${this.constructor.name}: onClickDate(${name})`); // eslint-disable-line no-console

    if (this.leadStatus !== 'open'
      || this.taskStatus !== null
      || this.readonly) return;

    // Imposta per quale campo verrà utilizzato il DatePicker
    this.datePickerName = name;

    // Imposta l'eventuale data precedentemente selezionata
    this.datePickerDate = this[name]
      ? moment(this[name]).toISOString(true).substring(0, 10)
      : null;

    // Apre il dialog per la selezione della data o della settimana
    switch (name) {
      case 'partialInstallationDate':
        if (forceSingle || this.partialInstallationDateConfirm === true) {
          // Va forzata la modalità "singola"

          // Passa alla modalità "singola"
          this.partialInstallationDateMode = 'single';
          this.$store.dispatch('updateTaskFields', {
            partialInstallationDateMode: 'single',
          });

          // Si prepara ad aprire il selettore della data in modalità "singola"
          this.initialMode = 'single';
          this.allowSingle = true;
          this.allowWeek = true;
        } else {
          // Altrimenti, abilita entrambe le modalità
          // (singola e settimana - inizialmente settimana se non impostata la modalità)
          this.initialMode = this.partialInstallationDateMode ?? 'week';
          this.allowSingle = true;
          this.allowWeek = true;
        }

        // this.weekPickerDialog = true;
        this.fullDatePickerDialog = true;
        break;

      case 'installationDate':
        if (forceSingle || this.installationDateConfirm === true) {
          // Va forzata la modalità "singola"

          // Passa alla modalità "singola"
          this.installationDateMode = 'single';
          this.$store.dispatch('updateTaskFields', {
            installationDateMode: 'single',
          });

          // Si prepara ad aprire il selettore della data in modalità "singola"
          this.initialMode = 'single';
          this.allowSingle = true;
          this.allowWeek = true;
        } else {
          // Altrimenti, abilita entrambe le modalità
          // (singola e settimana - inizialmente settimana se non impostata la modalità)
          this.initialMode = this.installationDateMode ?? 'week';
          this.allowSingle = true;
          this.allowWeek = true;
        }

        // this.weekPickerDialog = true;
        this.fullDatePickerDialog = true;
        break;

      default:
        this.datePickerDialog = true;
        break;
    }
  }

  onClearDate(name: string) {
    console.log(`${this.constructor.name}: onClearDate(${name})`); // eslint-disable-line no-console

    if (this.leadStatus !== 'open') return;

    const update = {} as { [key: string]: unknown };

    // Cancello la data nel modulo e nel prossimo aggiornamento
    if (name in this) {
      this[name] = null;
      update[name] = null;
    }

    // Modifico eventuali campi collegati
    switch (name) {
      case 'partialInstallationDate':
        this.partialInstallationDateConfirm = false;
        this.partialInstallationCompleted = false;
        update.partialInstallationDateConfirm = false;
        update.partialInstallationCompleted = false;
        break;

      case 'installationDate':
        this.installationDateConfirm = false;
        this.installationCompleted = false;
        update.installationDateConfirm = false;
        update.installationCompleted = false;
        break;

      default:
        break;
    }

    // Valido il modulo con le nuove informazioni inserite
    this.onValidate('completed');

    this.$store.dispatch('updateTaskFields', update);
  }

  onSelectDate(date: string | null, mode: 'single' | 'week' | null = null) {
    console.log(`${this.constructor.name}: onSelectDate() date = ${date} mode = ${mode}`); // eslint-disable-line no-console

    if (!this.datePickerName) return;

    // Chiudo il dialog per la selezione della data o della settimana
    this.datePickerDialog = false;
    this.weekPickerDialog = false;
    this.fullDatePickerDialog = false;

    const update = {} as { [key: string]: unknown };

    // Trasforma in un valore di tipo Date prima di restituirlo
    const selectedDate = date
      ? moment(date, 'YYYY-MM-DD').toDate()
      : null;

    // Inserisco la data nel modulo e nell'aggiornamento
    if (this.datePickerName in this) {
      this[this.datePickerName] = selectedDate;
      update[this.datePickerName] = this[this.datePickerName];
    }

    // Modifico eventuali campi collegati
    switch (this.datePickerName) {
      case 'partialInstallationDate':
        if (mode) {
          this.partialInstallationDateMode = mode;
          update.partialInstallationDateMode = mode;
        }
        break;

      case 'installationDate':
        if (mode) {
          this.installationDateMode = mode;
          update.installationDateMode = mode;
        }
        break;

      default:
        break;
    }

    // Valido il modulo con le nuove informazioni inserite
    this.onValidate('completed');

    this.$store.dispatch('updateTaskFields', update);
  }

  onFieldUpdated(field: string) {
    console.log(`${this.constructor.name}: onFieldUpdated(${field})`); // eslint-disable-line no-console

    const update = {} as { [key: string]: unknown };

    // Aggiorna sempre il campo specificato
    update[field] = this[field] ?? '';

    // Aggiorna eventuali campi collegati, eccezioni, ecc.
    switch (field) {
      default:
        break;
    }

    this.$store.dispatch('updateTaskFields', update);
  }

  onMissingFinishingsStatusUpdate(val: BoxStatusType | null) {
    this.missingFinishingsStatus = val;
    this.onFieldUpdated('missingFinishingsStatus');
  }

  onMissingFinishingsNotesUpdate(val: string | null) {
    this.missingFinishingsNotes = val;
    this.onFieldUpdated('missingFinishingsNotes');
  }

  onSubUpdated() {
    console.log(`${this.constructor.name}: onSubUpdated()`); // eslint-disable-line no-console

    const update = {} as { [key: string]: unknown };

    if (this.subValue === 'signed') {
      update.subDefined = true;
      update.subSent = true;
      update.subSigned = true;
    } else if (this.subValue === 'sent') {
      update.subDefined = true;
      update.subSent = true;
      update.subSigned = false;
    } else if (this.subValue === 'defined') {
      update.subDefined = true;
      update.subSent = false;
      update.subSigned = false;
    } else {
      update.subDefined = false;
      update.subSent = false;
      update.subSigned = false;
    }

    this.$store.dispatch('updateTaskFields', update);
  }

  // Effetta la validazione del modulo
  async onValidate(status: 'completed' | 'error') {
    console.log(`${this.constructor.name}: onValidate(${status})`); // eslint-disable-line no-console

    // Sceglie se validare il modulo per un completamento o per un fallimento
    this.selectedStatus = status;

    // Avvia l'effettiva validazione
    const form = this.$refs.form as Vue & {
      validate: () => boolean;
      reset: () => boolean;
    };

    if (form) {
      const result = form.validate();
      console.log(`${this.constructor.name}: onValidate() form.validate() =`, result); // eslint-disable-line no-console
    } else {
      console.error(`${this.constructor.name}: onValidate() form =`, form); // eslint-disable-line no-console
      console.error(`${this.constructor.name}: onValidate() this.$refs =`, this.$refs); // eslint-disable-line no-console
    }

    return Promise.resolve();
  }

  onEdit() {
    console.log(`${this.constructor.name}: onEdit()`); // eslint-disable-line no-console

    this.$emit('edit');
  }

  onReset() {
    console.log(`${this.constructor.name}: onReset()`); // eslint-disable-line no-console

    this.$emit('reset');
  }
}
